import { Banner, Button, LoadingWand, SearchBar } from '@ordr-as/umami-react'
import CreatePaymentFeeDialog from 'components/CreatePaymentFeeDialog'
import PinCodeDialog from 'components/PinCodeDialog'
import { usePaymentFees } from 'hooks/usePaymentFeeApi'
import OverviewPageTemplate from 'pages/OverviewPageTemplate'
import { useEffect, useMemo, useState } from 'react'
import { PaymentFeeDto } from '../../../generated-api'
import PaymentFeeList from './PaymentFeeList'

type FeeTab = 'global' | 'location-overrides'

const PaymentFeesOverview = () => {
    const [filteredPaymentFees, setFilteredPaymentFees] = useState<PaymentFeeDto[]>([])
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [createDialogIsOpen, setCreateDialogIsOpen] = useState(false)
    const [selectedFee, setSelectedFee] = useState<PaymentFeeDto | null>(null)
    const [selectedTab, setSelectedTab] = useState<FeeTab>('global')
    const [selectedCurrency, setSelectedCurrency] = useState<string>('')
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('')
    const [pinDialogIsOpen, setPinDialogIsOpen] = useState(false)
    const [pendingGlobalFeeEdit, setPendingGlobalFeeEdit] = useState<PaymentFeeDto | null>(null)
    const [pendingGlobalFeeCreate, setPendingGlobalFeeCreate] = useState(false)

    const { data: paymentFees, isLoading, isSuccess, isError, error } = usePaymentFees()

    const handleFeeClick = (fee: PaymentFeeDto) => {
        // Check if this is a global fee (no locationId)
        const isGlobalFee = fee.locationId === null || fee.locationId === undefined

        if (isGlobalFee) {
            // Show PIN dialog for global fees
            setPendingGlobalFeeEdit(fee)
            setPinDialogIsOpen(true)
            return
        }

        // For location-specific fees, open dialog directly
        setSelectedFee(fee)
        setCreateDialogIsOpen(true)
    }

    const handleCreateClick = () => {
        // Check if we're on the global tab - if so, require PIN first
        if (selectedTab === 'global') {
            setPendingGlobalFeeCreate(true)
            setPinDialogIsOpen(true)
            return
        }

        // For location-specific fees, open dialog directly
        setCreateDialogIsOpen(true)
    }

    const handlePinVerified = () => {
        setPinDialogIsOpen(false)

        if (pendingGlobalFeeEdit) {
            // User is editing an existing global fee
            setSelectedFee(pendingGlobalFeeEdit)
            setCreateDialogIsOpen(true)
            setPendingGlobalFeeEdit(null)
        }

        if (pendingGlobalFeeCreate) {
            // User is creating a new global fee
            setCreateDialogIsOpen(true)
            setPendingGlobalFeeCreate(false)
        }
    }

    const handlePinCancelled = () => {
        setPinDialogIsOpen(false)
        setPendingGlobalFeeEdit(null)
        setPendingGlobalFeeCreate(false)
    }

    const handleCloseDialog = () => {
        setSelectedFee(null)
        setCreateDialogIsOpen(false)
    }

    // Extract unique currencies and payment methods for filter dropdowns
    const { uniqueCurrencies, uniquePaymentMethods } = useMemo(() => {
        if (!paymentFees) {
            return { uniqueCurrencies: [], uniquePaymentMethods: [] }
        }

        const feesArray = Array.from(paymentFees)
        const currencies = [...new Set(feesArray.map((pf) => pf.currency))].sort()
        const methods = [...new Set(feesArray.map((pf) => pf.paymentMethod))].sort()

        return { uniqueCurrencies: currencies, uniquePaymentMethods: methods }
    }, [paymentFees])

    // Separate global fees from location overrides
    const { globalFees, locationOverrides } = useMemo(() => {
        if (!paymentFees) {
            return { globalFees: [], locationOverrides: [] }
        }

        const feesArray = Array.from(paymentFees)
        const global = feesArray.filter((pf) => pf.locationId === null || pf.locationId === undefined)
        const location = feesArray.filter((pf) => pf.locationId !== null && pf.locationId !== undefined)

        return { globalFees: global, locationOverrides: location }
    }, [paymentFees])

    // Filter fees based on search query, selected tab, and dropdown filters
    useEffect(() => {
        if (!paymentFees) {
            return
        }

        const trimmedSearchQuery = searchQuery.trim()
        const searchQueryLowerCase = trimmedSearchQuery.toLowerCase()

        // Choose the appropriate fee set based on selected tab
        const feesToFilter = selectedTab === 'global' ? globalFees : locationOverrides

        const filtered = feesToFilter.filter((pf) => {
            // Search query filter
            const matchesSearch =
                pf.paymentProvider.toLowerCase().includes(searchQueryLowerCase) ||
                pf.paymentMethod.toLowerCase().includes(searchQueryLowerCase) ||
                pf.currency.toLowerCase().includes(searchQueryLowerCase) ||
                (pf.locationId && pf.locationId.toString().includes(searchQueryLowerCase))

            // Currency filter
            const matchesCurrency = !selectedCurrency || pf.currency === selectedCurrency

            // Payment method filter
            const matchesPaymentMethod = !selectedPaymentMethod || pf.paymentMethod === selectedPaymentMethod

            return matchesSearch && matchesCurrency && matchesPaymentMethod
        })

        setFilteredPaymentFees(filtered)
    }, [paymentFees, searchQuery, selectedTab, selectedCurrency, selectedPaymentMethod, globalFees, locationOverrides])

    return (
        <OverviewPageTemplate heading="Payment Fees">
            {isLoading && <LoadingWand className="mx-auto" />}

            {isError && (
                <Banner kind="error" onClose={() => null} stretch="parent" className="my-3">
                    {(error as Error).message}
                </Banner>
            )}

            {isSuccess && (
                <div className="py-2 max-w-full">
                    {paymentFees.size === 0 ? (
                        <div className="flex flex-col gap-4">
                            <Banner kind="warn" stretch="parent" onClose={() => null}>
                                There are no payment fees configured
                            </Banner>
                            <Button kind="primary" onClick={handleCreateClick}>
                                Create Payment Fee
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4 flex gap-2">
                                <SearchBar
                                    value={searchQuery}
                                    placeholderText="Search provider, method, currency, or location ID"
                                    aria-label="Search payment fees"
                                    setValue={(text) => setSearchQuery(text)}
                                    setActive={() => null}
                                    fullWidth
                                />
                                <Button kind="primary" onClick={handleCreateClick}>
                                    Create Payment Fee
                                </Button>
                            </div>

                            <div className="mb-4 flex gap-2">
                                <div className="flex-1">
                                    <label className="block text-small font-medium text-grey-darkest mb-1">
                                        Filter by Currency
                                    </label>
                                    <select
                                        className="w-full border border-grey-light rounded p-2"
                                        value={selectedCurrency}
                                        onChange={(e) => setSelectedCurrency(e.target.value)}
                                    >
                                        <option value="">All Currencies</option>
                                        {uniqueCurrencies.map((currency) => (
                                            <option key={currency} value={currency}>
                                                {currency}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-small font-medium text-grey-darkest mb-1">
                                        Filter by Payment Method
                                    </label>
                                    <select
                                        className="w-full border border-grey-light rounded p-2"
                                        value={selectedPaymentMethod}
                                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                    >
                                        <option value="">All Payment Methods</option>
                                        {uniquePaymentMethods.map((method) => (
                                            <option key={method} value={method}>
                                                {method}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {(selectedCurrency || selectedPaymentMethod) && (
                                    <div className="flex items-end">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedCurrency('')
                                                setSelectedPaymentMethod('')
                                            }}
                                            className="text-small text-purple hover:text-purple-dark px-2 py-2 whitespace-nowrap"
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="flex border-b border-grey-light">
                                <button
                                    type="button"
                                    onClick={() => setSelectedTab('global')}
                                    className={`px-4 py-2 font-medium transition-colors ${
                                        selectedTab === 'global'
                                            ? 'text-purple border-b-2 border-purple'
                                            : 'text-grey-dark hover:text-grey-darkest'
                                    }`}
                                >
                                    Global Fees ({globalFees.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSelectedTab('location-overrides')}
                                    className={`px-4 py-2 font-medium transition-colors ${
                                        selectedTab === 'location-overrides'
                                            ? 'text-purple border-b-2 border-purple'
                                            : 'text-grey-dark hover:text-grey-darkest'
                                    }`}
                                >
                                    Location Overrides ({locationOverrides.length})
                                </button>
                            </div>

                            <div className="mt-4">
                                <PaymentFeeList paymentFees={filteredPaymentFees} onFeeClick={handleFeeClick} />
                            </div>
                        </>
                    )}
                </div>
            )}

            <CreatePaymentFeeDialog
                dialogIsOpen={createDialogIsOpen}
                onClose={handleCloseDialog}
                prefillFee={selectedFee}
            />

            <PinCodeDialog
                isOpen={pinDialogIsOpen}
                onSuccess={handlePinVerified}
                onCancel={handlePinCancelled}
                title="Global Fee Authorization Required"
                message={
                    pendingGlobalFeeCreate
                        ? 'You are about to create a global payment fee that impacts company earnings.'
                        : 'You are about to edit a global payment fee that impacts company earnings.'
                }
            />
        </OverviewPageTemplate>
    )
}

export default PaymentFeesOverview
